import React from "react";
import { Link } from "react-router-dom";
import { Avatar } from "./Avatar";

/**
 * FamilyAvatarBubbles — Apple-Messages-style group-chat avatar cluster.
 *
 * Renders the current user's avatar as the big "main" bubble (centered,
 * ringed in QFresh teal so it still feels like a button to Profile) with
 * up to four other household members floating around it in smaller
 * bubbles. Bubble sizes and positions are derived from how many other
 * members there are so the cluster always feels balanced and friendly
 * rather than empty.
 *
 * Visual rules:
 *   • The current user is always the largest, centered bubble.
 *   • Other members render in the 4 corners: top-left, top-right,
 *     bottom-left, bottom-right (clockwise from TL).
 *   • If there are more than 4 other members we show 3 photos + a "+N"
 *     pill in the bottom-right slot so the cluster never overflows.
 *   • Members without an avatar fall back to a soft pastel circle with
 *     their first initial — gives the cluster colour even for fresh
 *     households where nobody has uploaded a photo yet.
 *
 * Hit target:
 *   The whole cluster is wrapped in a Link to /profile so the main
 *   bubble keeps acting as the existing "tap photo → profile" shortcut.
 *
 * `members` is the array of household-member objects from `/households/me`
 * (or wherever — caller is responsible for filtering OUT the current user).
 */

// Pastel backgrounds for initial-based bubbles. We pick deterministically from
// a small palette so the same member always gets the same colour across
// renders, while different members feel visually distinct.
const PASTELS = [
  { bg: "#E8D5F2", fg: "#5A3A8A" },  // lavender
  { bg: "#D6EBD9", fg: "#2E5C3A" },  // mint
  { bg: "#D2E6F5", fg: "#1F4D72" },  // sky
  { bg: "#FCEBC8", fg: "#7A5418" },  // butter
  { bg: "#FAD8D8", fg: "#7B2D2D" },  // blush
  { bg: "#D5E7E4", fg: "#1F5566" },  // sage (QFresh-adjacent)
];
const pastelFor = (m) => {
  // Hash the member's id (or name fallback) into the palette so each
  // member is stable across sessions even when avatar_url isn't set.
  const key = (m?.id || m?.name || m?.email || "").toString();
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return PASTELS[Math.abs(h) % PASTELS.length];
};

const InitialBubble = ({ member, sizePx }) => {
  const pastel = pastelFor(member);
  const initial = ((member?.name || member?.email || "?").trim().charAt(0) || "?").toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center font-heading font-bold border-2 border-white shadow-sm"
      style={{
        width: sizePx,
        height: sizePx,
        backgroundColor: pastel.bg,
        color: pastel.fg,
        fontSize: Math.max(11, Math.round(sizePx * 0.42)),
      }}
      data-testid={`family-bubble-initial-${member?.id}`}
      title={member?.name || member?.email}
    >
      {initial}
    </div>
  );
};

const MemberBubble = ({ member, sizePx, testid }) => {
  if (member?.avatar_url) {
    return (
      <img
        src={member.avatar_url}
        alt={member.name || "member"}
        title={member.name || member.email}
        className="rounded-full object-cover border-2 border-white shadow-sm"
        style={{ width: sizePx, height: sizePx }}
        data-testid={testid}
        draggable={false}
      />
    );
  }
  return <InitialBubble member={member} sizePx={sizePx} />;
};

/**
 * MemberAvatar — public re-export of the photo-or-pastel-initial circle so
 * the Household page's member list can render the same visual style. Pulls
 * the avatar from `member.avatar_url` (set by `_serialize_household`) and
 * falls back to a deterministic pastel circle with the member's first
 * initial when no photo is uploaded.
 *
 * `border` controls whether to draw the small white ring around the
 * circle (on by default — looks great floating against cards, but you can
 * turn it off when the avatar already sits inside its own border, e.g.
 * the Profile page header).
 */
export const MemberAvatar = ({ member, size = 36, border = true, testid }) => {
  const className = `rounded-full object-cover ${border ? "border-2 border-white shadow-sm" : ""}`;
  if (member?.avatar_url) {
    return (
      <img
        src={member.avatar_url}
        alt={member.name || "member"}
        title={member.name || member.email}
        className={className}
        style={{ width: size, height: size }}
        data-testid={testid}
        draggable={false}
      />
    );
  }
  const pastel = pastelFor(member);
  const initial = ((member?.name || member?.email || "?").trim().charAt(0) || "?").toUpperCase();
  return (
    <div
      className={`rounded-full flex items-center justify-center font-heading font-bold ${border ? "border-2 border-white shadow-sm" : ""}`}
      style={{
        width: size,
        height: size,
        backgroundColor: pastel.bg,
        color: pastel.fg,
        fontSize: Math.max(11, Math.round(size * 0.42)),
      }}
      data-testid={testid}
      title={member?.name || member?.email}
    >
      {initial}
    </div>
  );
};

const OverflowBubble = ({ count, sizePx }) => (
  <div
    className="rounded-full flex items-center justify-center bg-[#1F5566] text-white font-heading font-bold border-2 border-white shadow-sm"
    style={{
      width: sizePx,
      height: sizePx,
      fontSize: Math.max(11, Math.round(sizePx * 0.38)),
    }}
    data-testid="family-bubble-overflow"
  >
    +{count}
  </div>
);

/**
 * Compute size + position of each side bubble based on how many other
 * members exist. Returns an array of inline-style objects keyed to the
 * corners we want to fill.
 *
 * The "frame" the cluster sits in is `CLUSTER_PX × CLUSTER_PX` centered
 * around the main bubble. Side bubbles are placed as `position: absolute`
 * with offsets so they overlap the main bubble naturally.
 */
const CLUSTER_PX = 120;     // overall bounding box for the cluster
const MAIN_PX = 78;         // diameter of the centered "main" (current user) avatar

const sideBubbleLayout = (count) => {
  // count = number of OTHER members we'll be rendering (incl. overflow pill)
  // Slots, in fill order: top-right, top-left, bottom-left, bottom-right
  const positions = [
    { top: -4,  right: -2 },
    { top: 6,   left: -4 },
    { bottom: -2, left: 4 },
    { bottom: 6, right: -4 },
  ];
  // Size shrinks gently as we add more bubbles so 4 still fit comfortably.
  const sizeByCount = { 1: 38, 2: 36, 3: 34, 4: 32 };
  const size = sizeByCount[count] || 32;
  return { positions, size };
};

export const FamilyAvatarBubbles = ({
  currentUser,
  members = [],
  to = "/profile",
  mainSize = MAIN_PX,
  testid = "family-bubbles",
}) => {
  // Exclude the current user from the side bubbles — they're always the main.
  const others = (members || []).filter((m) => m?.id && m.id !== currentUser?.id);
  const shownOthers = others.slice(0, 4);
  const overflow = Math.max(0, others.length - 3);
  // If we'd otherwise show exactly 4 and there are MORE than 4 others, swap
  // the 4th real bubble for a "+N" overflow pill so the count never lies.
  const renderItems = (() => {
    if (others.length <= 4) return shownOthers.map((m) => ({ type: "member", member: m }));
    const items = shownOthers.slice(0, 3).map((m) => ({ type: "member", member: m }));
    items.push({ type: "overflow", count: overflow });
    return items;
  })();

  const { positions, size } = sideBubbleLayout(renderItems.length);

  // No other members → just the main avatar (centered, no cluster frame).
  // We still want the same visual size as the multi-member case so the
  // header layout doesn't reflow when a member joins/leaves.
  if (renderItems.length === 0) {
    return (
      <Link
        to={to}
        data-testid={testid}
        aria-label="Open profile"
        className="shrink-0 inline-flex items-center justify-center rounded-full ring-2 ring-[#277A82] hover:ring-[#1F5566] hover:shadow-md transition-all"
        style={{ width: mainSize, height: mainSize }}
      >
        <Avatar user={currentUser} size={mainSize} testId="dashboard-avatar" />
      </Link>
    );
  }

  return (
    <Link
      to={to}
      data-testid={testid}
      aria-label="Open profile"
      className="shrink-0 relative block"
      style={{ width: CLUSTER_PX, height: CLUSTER_PX }}
    >
      {/* Centered main bubble */}
      <div
        className="absolute inline-flex items-center justify-center rounded-full ring-2 ring-[#277A82] hover:ring-[#1F5566] hover:shadow-md transition-all"
        style={{
          width: mainSize,
          height: mainSize,
          top: (CLUSTER_PX - mainSize) / 2,
          left: (CLUSTER_PX - mainSize) / 2,
        }}
      >
        <Avatar user={currentUser} size={mainSize} testId="dashboard-avatar" />
      </div>

      {/* Side bubbles in their assigned slots */}
      {renderItems.map((it, idx) => (
        <div
          key={it.type === "overflow" ? "overflow" : it.member.id}
          className="absolute"
          style={positions[idx]}
        >
          {it.type === "overflow" ? (
            <OverflowBubble count={it.count} sizePx={size} />
          ) : (
            <MemberBubble
              member={it.member}
              sizePx={size}
              testid={`family-bubble-${it.member.id}`}
            />
          )}
        </div>
      ))}
    </Link>
  );
};

/**
 * AirbnbAvatarStack — overlapping circle row used in the Household row on
 * the Profile page. Current user comes FIRST (left-most, on top), then
 * each subsequent member peeks out from behind by a fixed offset. If
 * there are more members than slots, the last slot becomes a "+N" pill
 * in QFresh teal so the count still adds up.
 *
 * Same pastel-initial fallback as the bubble cluster for any member that
 * doesn't have a `avatar_url` yet.
 */
export const AirbnbAvatarStack = ({
  currentUser,
  members = [],
  size = 30,
  maxVisible = 3,
  testid = "airbnb-stack",
}) => {
  const others = (members || []).filter((m) => m?.id && m.id !== currentUser?.id);
  const visibleOthers = others.slice(0, maxVisible - 1);
  const overflow = others.length - visibleOthers.length;
  // We render the current user first, then up to (maxVisible-1) other members,
  // then optionally a "+N" pill for the rest. Each subsequent circle is
  // pulled left by ~35% of its diameter so they overlap like an Airbnb host stack.
  const overlap = Math.round(size * 0.35);
  const items = [
    { type: "self", user: currentUser },
    ...visibleOthers.map((m) => ({ type: "member", member: m })),
    ...(overflow > 0 ? [{ type: "overflow", count: overflow }] : []),
  ];

  return (
    <div className="inline-flex items-center" data-testid={testid}>
      {items.map((it, idx) => (
        <div
          key={it.type === "overflow" ? "overflow" : it.type === "self" ? "self" : it.member.id}
          style={{ marginLeft: idx === 0 ? 0 : -overlap, zIndex: items.length - idx }}
          className="relative"
        >
          {it.type === "self" ? (
            it.user?.avatar_url ? (
              <img
                src={it.user.avatar_url}
                alt={it.user.name || "you"}
                className="rounded-full object-cover border-2 border-white shadow-sm"
                style={{ width: size, height: size }}
                data-testid={`${testid}-self`}
                draggable={false}
              />
            ) : (
              <InitialBubble member={it.user} sizePx={size} />
            )
          ) : it.type === "overflow" ? (
            <OverflowBubble count={it.count} sizePx={size} />
          ) : (
            <MemberBubble
              member={it.member}
              sizePx={size}
              testid={`${testid}-member-${it.member.id}`}
            />
          )}
        </div>
      ))}

      {/* Solo case: render a hollow "add member" bubble peeking from behind
          so the row visually invites the user to invite someone. */}
      {others.length === 0 && (
        <div
          style={{ marginLeft: -overlap, zIndex: 0 }}
          className="relative"
          data-testid={`${testid}-invite`}
        >
          <div
            className="rounded-full border-2 border-dashed border-[#8A93A6] bg-[#F5F3ED] text-[#8A93A6] flex items-center justify-center font-bold"
            style={{ width: size, height: size, fontSize: Math.round(size * 0.55) }}
            title="Invite someone"
          >
            +
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyAvatarBubbles;
